/**
 * Auth Service
 * bridge between the MSAL service and the application
 */
import type { Signal, WritableSignal } from '@angular/core';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivationEnd, Router } from '@angular/router';
import type { MsalGuardConfiguration } from '@azure/msal-angular';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import type {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  EventError,
  EventMessage,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import {
  BrowserAuthError,
  BrowserCacheLocation,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import type { Observable } from 'rxjs';
import {
  catchError,
  combineLatest,
  concat,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  map,
  merge,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';

import type { Maybe } from '@evc/web-components';
import { deepMerge, ENV_VARIABLES, ToastService } from '@evc/web-components';

import {
  PlatformConfigService,
  PlatformEnvExtended,
} from '../../../../features/config/config.service';
import {
  AUTH_INITIALIZATION_STRATEGIES,
  AvailableLanguages,
  ENVIRONMENT,
} from '../../../../features/config/env.type';
import { I18nPlatformService } from '../../../../features/i18n/i18n.service';
import { activatedRoute$ } from '../../../../features/utilities/router.utils';
import { AUTH_REQUEST_TYPE, authRequestFactory } from '../auth.config';
import type {
  AuthConfig,
  AuthenticationResultWithClaims,
  AuthStateReplay,
  IdTokenClaims,
} from '../auth.type';
import {
  ADB2CErrors,
  AUTH_STATE_REPLAY_KEY,
  Authorities,
  RedirectNotifications,
  SSO_PROVIDERS,
} from '../auth.type';
import { POST_LOGIN_REDIRECT_URL, TOKEN_CLAIM_ORGANIZATION_ID } from '../providers/auth.providers';

// ? Today enforce sync accross tab using localStorage
// ? but may change to sessionStorage if want unsynced auth between tabs
const CACHE_LOCATION = BrowserCacheLocation.LocalStorage;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #msalService = inject(MsalService, { optional: true });
  getMsalService(): MsalService | null {
    return this.#msalService;
  }
  get #msalInstance() {
    return this.#msalService?.instance;
  }
  #msalBroadcastService = inject(MsalBroadcastService, { optional: true });
  #msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG, { optional: true });
  #platformConfigService = inject(PlatformConfigService);
  #toastService = inject(ToastService);
  #i18nService = inject(I18nPlatformService);
  #destroyRef = inject(DestroyRef);
  #router = inject(Router, { optional: true });
  // optional to ease tests
  #env = inject(ENV_VARIABLES, { optional: true }) as unknown as PlatformEnvExtended;
  #TOKEN_CLAIM_ORGANIZATION_ID = inject(TOKEN_CLAIM_ORGANIZATION_ID, { optional: true }) as
    WritableSignal<Maybe<string>> | undefined;
  #postLoginRedirectUrl = inject(POST_LOGIN_REDIRECT_URL, { optional: true }) as
    WritableSignal<Maybe<string>> | undefined;

  /** if isOffline - will not attempt fetch token on initialization */
  isOffline = signal<boolean>(false);

  /** after auth init flow : true|false if connected or not ;
   * undefined meaning flow still in progress */
  #isAuthenticated = signal<Maybe<boolean>>(undefined);
  isAuthenticated = this.#isAuthenticated.asReadonly();

  // please use init() - it will return this promise
  #initializationPromise: Promise<boolean /* isAuthenticated */> | undefined;

  // /** emits when isAuthenticated is defined (true/false) */
  onComplete$ = toObservable(this.#isAuthenticated).pipe(
    filter((isAuthenticated: Maybe<boolean>) => isAuthenticated !== undefined),
    map((isAuthenticated) => isAuthenticated as boolean),
    takeUntilDestroyed(this.#destroyRef),
  );

  #allAcounts = signal<AccountInfo[]>([]);
  #activeAccount = signal<Maybe<AccountInfo>>(undefined);
  accountFound = computed(() => this.#allAcounts().length > 0);

  #accessToken = signal<string | undefined>(undefined);
  accessToken = this.#accessToken.asReadonly();
  accessToken$ = toObservable(this.#accessToken);

  #authErrorSubject = new ReplaySubject<EventError>(1);
  public readonly authError$ = this.#authErrorSubject.asObservable();

  #config!: AuthConfig;
  get config(): AuthConfig {
    return this.#config;
  }
  get clientId(): string {
    return this.config?.clientId;
  }

  #loginHint = signal<Maybe<string>>(undefined);
  get loginHint(): Signal<Maybe<string>> {
    return this.#loginHint.asReadonly();
  }
  set loginHint(loginHint: Maybe<string>) {
    this.#loginHint.set(loginHint);
  }

  // You may store state before a login request and retrieve it after the redirect
  #state = signal<Maybe<Record<string, unknown>>>(undefined);
  state = this.#state.asReadonly();
  shouldPromptOrganizationSelection = computed(() => Boolean(this.#state()?.promptOrgSelection));

  dismissOrganizationSelectionPrompt(): void {
    this.#state.update((s) => (s ? { ...s, promptOrgSelection: false } : s));
  }

  beforeLoginHook = signal<(() => Observable<void> | Promise<void> | void) | undefined>(undefined);

  #isRedirectingToCreateOrganization = signal(false);
  isRedirectingToCreateOrganization = this.#isRedirectingToCreateOrganization.asReadonly();

  /** login request params stored in redirect state to potentially retry request on specific fails (captcha cancel) */
  #replayState = signal<Maybe<AuthStateReplay>>(undefined);

  #idTokenClaims = signal<Maybe<IdTokenClaims>>(undefined);
  idTokenClaims = this.#idTokenClaims.asReadonly();

  useSSOProvider = computed<boolean>(
    () => (this.#idTokenClaims()?.selectedSsoProvider ?? SSO_PROVIDERS.NONE) !== SSO_PROVIDERS.NONE,
  );
  organizationId = computed<Maybe<string>>(() => this.#idTokenClaims()?.organizationId);

  licenseIds = computed<Maybe<string[]>>(() =>
    (this.#idTokenClaims()?.licenseIds ?? '').split(','),
  );
  licenseProductFeatureFlags = computed<Maybe<string[]>>(() =>
    (this.#idTokenClaims()?.licenseProductFeatureFlags ?? '').split(','),
  );
  licenseMessage = computed<Maybe<string[]>>(() =>
    (this.#idTokenClaims()?.licenseMessage ?? '').split(','),
  );
  #language = signal<string | undefined>(undefined);
  set language(lang: string) {
    this.#language.set(lang);
  }
  #waitLang$: Observable<string> = toObservable(this.#language).pipe(
    takeUntilDestroyed(this.#destroyRef),
    filter((lang) => lang !== undefined && lang !== ''),
    map((lang?: string) => lang!),
  );

  /** May enforce login if current route have MSALGuard */
  isRouteProtected$(): Observable<boolean> {
    return (() => {
      if (!this.#router) return of(false);

      return concat(
        activatedRoute$(this.#router),
        this.#router.events.pipe(
          filter((event) => event instanceof ActivationEnd),
          map((route) => route?.snapshot),
        ),
      ).pipe(
        map((route) => route.routeConfig!.canActivate || []),
        map((guards) => guards.includes(MsalGuard)),
      );
    })().pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /** Must wait this before attempting any interactions (login, requestAccesstoken...) */
  canPerformInteraction$ = (this.#msalBroadcastService?.inProgress$ ?? new Subject()).pipe(
    map((status) => status === InteractionStatus.None),
    distinctUntilChanged(),
  );
  canPerformInteraction = toSignal(this.canPerformInteraction$);

  constructor() {
    if (!this.#platformConfigService.useConnect()) return;
    if (!this.#msalService || !this.#msalBroadcastService || !this.#msalGuardConfig) {
      throw new Error('AuthService : MSAL not provided');
    }

    this.#config = this.#platformConfigService.get('auth')!;

    if (this.#platformConfigService.getEnv('DEBUG_AUTH_FLOW')) {
      const env = this.#platformConfigService.getEnv('ENV');
      if (!env || env === ENVIRONMENT.production || env === ENVIRONMENT.staging) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).authService = this;
    }

    this.#getRedirectState();
  }

  /** Start our init flow
   * @returns Promise<boolean> : user is authenticated or not
   *
   * here we may wait 1st token acquisition ; only if needed !
   * otherwise we just return false as user is not authenticated on initilization
   *
   * (guard / interceptor) may trigger async auth
   * - you may like to subscribe to accessToken$ for later authentication change (or use isAuthenticated signal)
   *
   * * do not forget to call this on app init !
   */
  async init(): Promise<boolean> {
    if (!this.#config?.enabled) {
      this.#isAuthenticated.set(false);

      return false;
    }

    // if already initialized - skip this and just return the promise
    if (!this.#initializationPromise) {
      this.#msalInstance?.enableAccountStorageEvents(); // important because standalone

      this.#initializationPromise = firstValueFrom(this.#initializationFlow$());

      this.#handleMsalEvents();
      this.#syncAuthenticationStateAccrossTabs();
    }

    return this.#initializationPromise;
  }

  /** Simply wait auth completion (if needs auth)
   * @returns Promise<boolean> : user is authenticated or not
   *
   * this does not trigger any auth process
   * but useful for app initialization (loader until process complete)
   */
  #initializationFlow$(): Observable<boolean> {
    return this.#msalService!.handleRedirectObservable().pipe(
      catchError((error) => {
        this.#debug('initialization : ✗ error', error.message, { error });

        // catch this error early (prior to handleLoginFailure)
        // so we can replay the request before Guards trigger
        if (error.message === ADB2CErrors.CAPTCHA_CANCELLED) {
          const replayState = this.#replayState() ?? {};
          this.#debug('↻ CAPTCHA_CANCELLED - Replay Auth Request', replayState);
          return this.login(replayState);
        }

        this.#authErrorSubject.next(error);

        return of(false);
      }),
      switchMap(() => (this.accountFound() ? of(true) : this.isRouteProtected$())),
      switchMap((mayAttemptAuth) => {
        if (!mayAttemptAuth) {
          this.#debug('initialization : ... skip auth');

          return of(false);
        }

        this.#debug('initialization : ... wait auth');

        return this.#waitForToken$().pipe(
          catchError((error) => {
            this.#debug('initialization : ... skip auth :', error.message);

            return of(false);
          }),
        );
      }),
      tap((isUserAuthenticated: boolean) =>
        this.#debug(
          `initialization : ✓ DONE : user ${isUserAuthenticated ? 'is' : "isn't"} authenticated`,
        ),
      ),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  /**
   * Request access token routine
   * - Attempt silent token acquisition
   * - tokenFailure may chain with interactive method
   * - if no active account, may enforce logout
   */
  requestAccessToken(
    scopes: string[] = [],
    extra?: Partial<SilentRequest>,
  ): Observable<AuthenticationResult | undefined> {
    if (!this.canPerformInteraction()) {
      this.#debug('skip requestAccessToken: interaction in progress');

      return EMPTY;
    }

    const account = this.#retreiveActiveAccount();

    // may enforce logout if protected route
    if (!account) {
      this.#debug('requestAccessToken without accounts', { scopes, extra, account });

      return this.#handleNoActiveAccount();
    }

    const request = authRequestFactory(
      AUTH_REQUEST_TYPE.SILENT,
      {
        triggerType: 'service',
        environment: this.#env ?? ({} as PlatformEnvExtended),
        config: this.#config,
        scopes: scopes ?? [],
        lang: this.#language() as AvailableLanguages,
        toid: this.#retrieveToidFromClaimOrReloadedSession(extra?.forceRefresh === true),
      },
      {
        account,
        state: JSON.stringify(this.state()),
        ...extra,
      },
    ) as SilentRequest;

    this.#debug('acquireTokenSilent', { scopes, extra, account }, request);

    return this.#msalService!.acquireTokenSilent(request);
  }

  login(request?: Partial<RedirectRequest>): Observable<void> {
    if (!this.canPerformInteraction()) {
      this.#debug('skip login: interaction in progress');

      return EMPTY;
    }

    // HACK : creating a promise wrapped in observable allow us to call the redirect even if we do not subscribe to this.login()
    const loginPromise = firstValueFrom(
      this.#runBeforeLoginHook$().pipe(switchMap(() => this.#doLoginRedirect$(request))),
    );

    return from(loginPromise);
  }

  /** store authRequest main props in state (__replay)
   * so cen retreive them back on redirect - and maybe replay the request (e.g. captcha fail) */
  #enrichStateForReplay({
    authority,
    loginHint,
    redirectUri,
    prompt,
    redirectStartPage,
    state,
  }: RedirectRequest): string {
    const userState: Record<string, unknown> = (() => {
      if (!state) return {};
      try {
        return JSON.parse(state) ?? {};
      } catch {
        return {};
      }
    })();

    const replay: AuthStateReplay = {
      authority,
      loginHint,
      redirectUri,
      prompt,
      redirectStartPage,
    };

    return JSON.stringify({ ...userState, [AUTH_STATE_REPLAY_KEY]: replay });
  }

  /**
   * We force to pass an account so ADB can use Logout Front Channel to logout from any other apps (of same tenant)
   * @see https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/logout.md#logoutredirect
   */
  logout(logoutRequest?: EndSessionRequest): Observable<void> {
    if (!this.canPerformInteraction()) {
      this.#debug('skip logout: interaction in progress');

      return EMPTY;
    }

    const activeAccount = this.#msalInstance?.getActiveAccount();
    const homeAccount =
      activeAccount?.homeAccountId &&
      this.#msalInstance?.getAccountByHomeId(activeAccount.homeAccountId);
    const account = homeAccount || activeAccount;

    logoutRequest = { account, ...logoutRequest };
    const { onRedirectNavigate } = this.#config;
    if (onRedirectNavigate) {
      logoutRequest.onRedirectNavigate = onRedirectNavigate;
    }

    this.#debug('logout', { logoutRequest });

    if (logoutRequest.onRedirectNavigate) {
      this.#storeAccessToken(undefined);
      this.#TOKEN_CLAIM_ORGANIZATION_ID?.set(undefined);
      this.#idTokenClaims.set(undefined);
      this.#isAuthenticated.set(false);
      this.#allAcounts.set([]);
      this.#activeAccount.set(undefined);
      this.#msalInstance?.setActiveAccount(null);
    }

    return this.#msalService!.logoutRedirect(logoutRequest);
  }

  resetPassword(partialRequest?: Partial<RedirectRequest>): Observable<void> {
    const authority = this.#config.b2cPolicies.authorities[Authorities.RESET_PASSWORD];

    const request = {
      loginHint: this.loginHint(),
      ...(partialRequest ?? {}),
      authority,
      prompt: 'login',
    };

    return this.#doLoginRedirect$(request);
  }

  /**
   * Handle organization creation flow
   * - Redirect to organization creation
   * - Expect to be re-redirected here after creation
   */
  redirectToCreateOrganization(redirectUri?: string): void {
    this.#isRedirectingToCreateOrganization.set(true);
    this.#waitLang$
      .pipe(
        take(1),
        tap(() => this.#debug('Redirecting to organization creation')),
        tap(() => {
          const createOrganizationAppURI = this.#config.redirects.organization;
          const adminAppURI = this.#platformConfigService.get('uris').admin;

          const createOrganizationThenRedirectHereURL = new URL(
            createOrganizationAppURI,
            window.location.origin,
          );
          createOrganizationThenRedirectHereURL.searchParams.set('lang', this.#language()!);
          createOrganizationThenRedirectHereURL.searchParams.set(
            'redirect_uri',
            redirectUri ?? adminAppURI,
          );
          const { back: backRedirectUri } = this.#config.redirects ?? {};
          if (backRedirectUri) {
            createOrganizationThenRedirectHereURL.searchParams.set(
              'back_redirect_uri',
              backRedirectUri,
            );
          }

          window.location.href = createOrganizationThenRedirectHereURL.toString();
        }),
      )
      .subscribe();
  }

  /**
   * Redirect to homepage with error message
   */
  redirectOut$(authFailCode: string): Observable<never> {
    const homepageRedirectUrl = new URL('/login', this.#platformConfigService.get('uris').homepage);
    homepageRedirectUrl.searchParams.set('lang', this.#language()!);
    homepageRedirectUrl.searchParams.set('authFailCode', encodeURIComponent(authFailCode));

    window.location.href = homepageRedirectUrl.toString();

    return EMPTY;
  }

  /**
   * ! ---------------- MSAL EVENTS ----------------
   */

  #handleMsalEvents() {
    merge(
      // msal init after redirect
      this.#handleInteractionProgress(),
      // login success
      this.#handleLoginSuccess(),
      this.#handleTokenAcquisitionSuccess(),
      // login failures
      this.#handleLoginFailure(),
      this.#handleTokenAcquisitionFailure(),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
  }

  #handleInteractionProgress(): Observable<'none'> {
    return this.#msalBroadcastService!.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      tap(() => {
        this.#restorePostLoginNavigation();
        this.#storeAccounts();

        const isOffline = this.isOffline();
        const accountFound = this.accountFound();
        const hasToken = !!this.#accessToken();
        const hasRedirectState = this.#state();
        const isAuthenticated = accountFound && hasToken;
        const { initializationStrategy, clientId } = this.#config;

        // if no account, guard will take care of it if needed
        // but if found cache account, we may need to fetch token (silent)
        const shouldFetchToken =
          accountFound &&
          !hasToken &&
          initializationStrategy !== AUTH_INITIALIZATION_STRATEGIES.NONE &&
          !isOffline &&
          !hasRedirectState;

        const forceRefresh =
          !hasRedirectState &&
          initializationStrategy === AUTH_INITIALIZATION_STRATEGIES.FORCE_REFRESH;

        this.#debug('event : IN_PROGRESS_NO_INTERACTION', {
          shouldFetchToken,
          isAuthenticated,
          accountFound,
          hasToken,
          initializationStrategy,
          isOffline,
          hasRedirectState,
          forceRefresh,
        });

        if (!shouldFetchToken) {
          this.#debug('in-progress done', { isAuthenticated });
          this.#isAuthenticated.set(isAuthenticated);

          return;
        }

        this.#debug('attempt silent login', { forceRefresh, clientId });

        this.requestAccessToken([clientId], { forceRefresh })
          .pipe(
            map(() => true),
            catchError(() => of(false)),
            tap((authenticated) => this.#isAuthenticated.set(authenticated)),
            tap((authenticated) => this.#debug('in-progress done', { authenticated })),
          )
          // eslint-disable-next-line rxjs-x/no-subscribe-in-pipe
          .subscribe();
      }),
    );
  }

  /** SpaNavigationClient stashes the pre-login deep-link instead of navigating immediately. */
  #restorePostLoginNavigation(): void {
    const target = this.#postLoginRedirectUrl?.();
    if (!target || !this.#router) return;

    this.#postLoginRedirectUrl!.set(undefined);
    this.#router.navigateByUrl(target, { replaceUrl: true });
  }

  #handleLoginSuccess(): Observable<AuthenticationResult> {
    return this.#msalBroadcastService!.msalSubject$.pipe(
      filter(
        ({ eventType }) =>
          eventType === EventType.LOGIN_SUCCESS || eventType === EventType.SSO_SILENT_SUCCESS,
      ),
      tap((event) => this.#debug('event ✓ LOGIN_SUCCESS', event)),
      map((event) => event.payload as AuthenticationResultWithClaims),
      switchMap(this.#mayRedirectIfUnsatisfiedToken$.bind(this)),
      tap((result: AuthenticationResultWithClaims) => {
        const { idTokenClaims, account, accessToken, state } = result;
        this.#msalInstance?.setActiveAccount(account);

        this.#processAuthenticationSuccess(accessToken, idTokenClaims, state);

        this.#debug('LOGIN_SUCCESS setActiveAccount', {
          idTokenClaims,
          account,
          accessToken,
          state,
        });
      }),
    );
  }

  #handleTokenAcquisitionSuccess(): Observable<AuthenticationResultWithClaims> {
    return this.#msalBroadcastService!.msalSubject$.pipe(
      filter(({ eventType }) => eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      debounceTime(100),
      tap((event) => this.#debug('event ✓ ACQUIRE_TOKEN_SUCCESS', event)),
      map((event) => event.payload as AuthenticationResultWithClaims),
      switchMap(this.#mayRedirectIfUnsatisfiedToken$.bind(this)),
      // Ensure active account is set (defensive against cross-tab race conditions)
      tap(({ account }: AuthenticationResultWithClaims) => {
        const currentActiveAccount = this.#msalInstance?.getActiveAccount();
        const accountMissmatch =
          !currentActiveAccount || currentActiveAccount.homeAccountId !== account.homeAccountId;

        if (accountMissmatch) {
          this.#msalInstance?.setActiveAccount(account);
          this.#debug('Restoring active account', {
            previous: currentActiveAccount?.homeAccountId,
            new: account.homeAccountId,
          });
        }
      }),
      switchMap((result: AuthenticationResultWithClaims) => {
        const requestedOrgId = this.organizationId();
        const receivedOrgId = result.idTokenClaims.organizationId;
        const activeAccount = this.#msalInstance?.getActiveAccount();

        const organizationMissmatch =
          requestedOrgId && receivedOrgId && requestedOrgId !== receivedOrgId;

        if (organizationMissmatch) {
          const request = authRequestFactory(
            AUTH_REQUEST_TYPE.INTERACTIVE,
            {
              triggerType: 'service',
              environment: this.#env ?? ({} as PlatformEnvExtended),
              config: this.#config,
              lang: this.#language() as AvailableLanguages,
              toid: this.#retrieveToidFromClaimOrReloadedSession(false),
            },
            {
              forceRefresh: true,
              account: activeAccount,
              state: JSON.stringify(this.state()),
            } as Partial<SilentRequest>,
          ) as SilentRequest;

          this.#debug(
            'handleTokenAcquisitionSuccess',
            '⚠️ Organization mismatch detected - request token with correct organization',
            {
              requested: requestedOrgId,
              received: receivedOrgId,
              account: activeAccount,
            },
          );

          if (this.#msalGuardConfig!.interactionType === InteractionType.Popup) {
            return this.#msalService!.acquireTokenPopup(request).pipe(
              map((popupResult) => popupResult as AuthenticationResultWithClaims),
            );
          }

          return this.#msalService!.acquireTokenRedirect(request).pipe(switchMap(() => EMPTY));
        }

        return of(result);
      }),
      tap((result: AuthenticationResultWithClaims) => {
        const { idTokenClaims, accessToken, state } = result;
        this.#processAuthenticationSuccess(accessToken, idTokenClaims, state);

        // Clear any session failure flags
        const { storageKeys } = this.#config;
        sessionStorage.removeItem(storageKeys.acquireTokenRedirectFailed);
      }),
    );
  }

  #mayRedirectIfUnsatisfiedToken$(
    result: AuthenticationResultWithClaims,
  ): Observable<AuthenticationResultWithClaims> {
    const { idTokenClaims } = result;

    const mustCreateOrganization = !idTokenClaims.organizationId && this.#config.forceOrganization;
    if (mustCreateOrganization) {
      const urlParams = new URLSearchParams(window.location.search);
      const alreadyAttempted = urlParams.get('reason');

      if (!alreadyAttempted) {
        this.redirectToCreateOrganization(window?.location.href);

        return EMPTY;
      }

      return this.#getAppName$().pipe(
        map((appName) =>
          this.#i18nService.t('errors.auth.organization_required', { name: appName }),
        ),
        switchMap(this.redirectOut$.bind(this)),
      );
    }

    const needLicense = !idTokenClaims.licenseIds && this.#config.forceLicense;
    if (needLicense) {
      // When organization selection is pending, skip the redirect so the user can pick
      // an organization that does have a license for this application.
      if (this.shouldPromptOrganizationSelection()) return of(result);

      return this.#getAppName$().pipe(
        map((appName) => {
          const errorCodes = (idTokenClaims.licenseMessage ?? '').split(',').filter(Boolean);
          const messages = errorCodes.map((code) =>
            this.#i18nService.t(`errors.auth.license_required.${code.trim()}`, { name: appName }),
          );

          return messages.length > 0
            ? messages.join('<br>')
            : this.#i18nService.t('errors.auth.license_required._default', { name: appName });
        }),
        switchMap(this.redirectOut$.bind(this)),
      );
    }

    return of(result);
  }

  #processAuthenticationSuccess(
    accessToken: string,
    idTokenClaims: IdTokenClaims,
    state?: string,
  ): void {
    const { sub: userId, organizationId } = idTokenClaims;
    const { storageKeys } = this.#config;

    this.#storeAccessToken(accessToken);
    this.#idTokenClaims.set(idTokenClaims as IdTokenClaims);
    this.#isAuthenticated.set(true);
    setTimeout(() => {
      this.#TOKEN_CLAIM_ORGANIZATION_ID?.set(organizationId);
    });

    if (state) this.#state.set(JSON.parse(state));

    // Sync authentication state across tabs
    localStorage.setItem(
      storageKeys.onAuthSessionChange,
      JSON.stringify({
        userId,
        organizationId,
      }),
    );

    // today use localStorage so everything is synced across tabs
    // this is to allow unsync accross tabs : allowing to stay on current toid on page reload
    // needs AUTH_ENABLE_SESSION_ON_RELOAD and using sessionsStorage
    if (
      organizationId &&
      this.#config.enableSessionOnReload &&
      CACHE_LOCATION !== BrowserCacheLocation.LocalStorage
    ) {
      sessionStorage.setItem(storageKeys.toid, organizationId);
    }
  }

  /** on login failure
   * - log errors and set ! authenticated state
   * * if canceled operation : reload the page to restart auth flow
   */
  #handleLoginFailure(): Observable<void> {
    return this.#msalBroadcastService!.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
      tap((event) => this.#debug('event ✗ LOGIN_FAILURE', event.error?.message, event)),
      map((event) => event.error),
      switchMap((error: EventError) => {
        if (error) {
          const { message } = error;

          const knownError = Object.keys(ADB2CErrors).find(
            (key) => message.indexOf(ADB2CErrors[key as keyof typeof ADB2CErrors]) > -1,
          );

          const errorMessage = knownError?.replace(/_/g, ' ').toLowerCase() ?? 'redirect failed';
          console.error(`Auth ${errorMessage} : ${message}`);

          // reloading allow to restart all flow
          // because guard stated fail but maybe we are still connected
          if (message.indexOf(ADB2CErrors.CANCELED_OPERATION) > -1) {
            window.location.reload();
          }
        }

        this.#isAuthenticated.set(false);

        return throwError(() => error);
      }),
    );
  }

  /**
   * on token aquisition failures
   * - ignore on public route
   * - ignore if login in progress
   * - try interactive method as fallback (expired refresh token)
   * - finaly, dead-end error redirect to auth-fail route (use failure guard)
   */
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  #handleTokenAcquisitionFailure(): Observable<AuthenticationResult | void> {
    return this.#msalBroadcastService!.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
      tap((event) => {
        const error = event.error;
        this.#debug('event ✗ ACQUIRE_TOKEN_FAILURE', error?.message, {
          errorCode: error && 'errorCode' in error ? error.errorCode : undefined,
          errorMessage: error?.message,
          subError: error && 'subError' in error ? error.subError : undefined,
          correlationId: error && 'correlationId' in error ? error.correlationId : undefined,
          timestamp: new Date().toISOString(),
          currentOrg: this.organizationId(),
          activeAccount: this.#msalInstance?.getActiveAccount()?.homeAccountId,
          event,
        });
      }),
      map((event) => event.error),

      switchMap((error: EventError) => {
        return combineLatest([this.isRouteProtected$(), this.canPerformInteraction$]).pipe(
          take(1),
          switchMap(([isProtected, canInteract]) => {
            // skip on public routes or login in progress
            if (!canInteract || !isProtected) {
              this.#debug('processTokenAcquisitionFailure : skip auth', {
                isProtected,
                canInteract,
              });

              return EMPTY;
            }

            // skip if already attempted interactive method (avoiding login loops)
            const { storageKeys } = this.#config;
            const hasTriedInteractive = sessionStorage.getItem(
              storageKeys.acquireTokenRedirectFailed,
            );
            if (hasTriedInteractive)
              sessionStorage.removeItem(storageKeys.acquireTokenRedirectFailed);
            const shouldAttemptInteractive =
              !hasTriedInteractive &&
              (error instanceof BrowserAuthError || error instanceof InteractionRequiredAuthError);

            this.#debug('processTokenAcquisitionFailure :', {
              hasTriedInteractive,
              shouldAttemptInteractive,
              isProtected,
              canInteract,
            });

            if (shouldAttemptInteractive) {
              sessionStorage.setItem(storageKeys.acquireTokenRedirectFailed, 'true'); // prevent login loops

              // Build request WITHOUT prompt parameter
              // - Microsoft will use existing SSO session if valid, or prompt if expired
              const request = authRequestFactory(
                AUTH_REQUEST_TYPE.INTERACTIVE,
                {
                  triggerType: 'service',
                  environment: this.#env ?? ({} as PlatformEnvExtended),
                  config: this.#config,
                  lang: this.#language() as AvailableLanguages,
                  toid: this.#retrieveToidFromClaimOrReloadedSession(false),
                },
                {
                  forceRefresh: true,
                  account: this.#msalInstance?.getActiveAccount() ?? undefined,
                  state: JSON.stringify(this.state()),
                },
              ) as SilentRequest;

              this.#debug(
                'processTokenAcquisitionFailure : attempt interactive',
                JSON.stringify(request),
                this.#msalInstance?.getActiveAccount(),
              );

              return this.#msalGuardConfig!.interactionType === InteractionType.Popup
                ? this.#msalService!.acquireTokenPopup(request)
                : this.#msalService!.acquireTokenRedirect(request);
            }

            this.#debug('processTokenAcquisitionFailure :', error);

            // ! DEAD END - Navigate to login failed route with error handling
            this.#authErrorSubject.next(error);

            const loginFailedRoute = this.#msalGuardConfig?.loginFailedRoute;
            if (loginFailedRoute && this.#router) {
              this.#router.navigate([loginFailedRoute]);
            } else {
              console.error('AuthService: No loginFailedRoute configured in MsalGuardConfig');
            }

            return throwError(() => error);
          }),
        );
      }),
    );
  }

  /**
   * Handle the case where there's no active account
   * - On public routes: just return undefined
   * - On protected routes: force logout to clear stale state - which redirects to public homepage
   */
  #handleNoActiveAccount(): Observable<undefined> {
    return this.isRouteProtected$().pipe(
      switchMap((isProtected) => {
        if (!isProtected) {
          return of(undefined);
        }

        this.#debug('No active account on protected route - forcing logout');

        return this.logout().pipe(
          catchError(() => of(undefined)),
          switchMap(() =>
            throwError(
              () => new Error('requestAccessToken: No active account found on protected route'),
            ),
          ),
        );
      }),
    );
  }

  /**
   * ! ---------------- PRIVATE ----------------
   */

  /**
   * show logs only if DEBUG_AUTH_FLOW is set to true
   */
  #debug(msg: string, ...args: unknown[]): void {
    if (!this.#platformConfigService.getEnv('DEBUG_AUTH_FLOW')) return;

    // eslint-disable-next-line no-console
    console.debug(`[auth] ${msg}`, ...args);
  }

  #storeAccessToken(accessToken?: string): void {
    if (accessToken === this.#accessToken() || accessToken === '') return;
    this.#accessToken.set(accessToken);
  }

  /**
   * use MSAL instance to retreive accounts and sync with local signals
   */
  #storeAccounts(): Maybe<AccountInfo> {
    const allAcounts = this.#msalInstance?.getAllAccounts() ?? [];
    this.#allAcounts.set(allAcounts);

    const activeAccount = this.#retreiveActiveAccount();
    this.#activeAccount.set(activeAccount);

    this.#debug('storeAccounts', { allAcounts, activeAccount });

    return activeAccount;
  }

  /** Retrieve the active account using fallbacks:
   * - from same app
   * - or first from allAccounts
   * - unless no account found, obviously
   */
  #retreiveActiveAccount(): Maybe<AccountInfo> {
    const allAccounts = this.#msalInstance?.getAllAccounts();

    if (!allAccounts) return undefined;

    const account = this.#msalInstance?.getActiveAccount();

    if (account) return account;

    const retreivedAccount =
      allAccounts.find(({ idTokenClaims }) => idTokenClaims?.aud === this.#config.clientId) ??
      allAccounts[0];

    this.#msalInstance?.setActiveAccount(retreivedAccount);

    this.#debug('restored active account', {
      accountId: retreivedAccount?.homeAccountId,
    });

    return retreivedAccount;
  }

  /**
   * Today using LocalStorage, we always retrieve the toid from the claim
   * (because LocalStorage is shared across tabs and sessions)
   *
   * maybe usefull logic to retrieve the toid from the sessionStorage
   * (only if AUTH_ENABLE_SESSION_ON_RELOAD is true using session storage)
   */
  #retrieveToidFromClaimOrReloadedSession(isFirstRequest: boolean): Maybe<string> {
    const fromClaim = this.organizationId();

    if (CACHE_LOCATION === BrowserCacheLocation.LocalStorage) return fromClaim;

    // --- below in case we want to use sessionStorage + AUTH_ENABLE_SESSION_ON_RELOAD ---

    const isBrowserReload =
      (performance.getEntriesByType('navigation')[0] as unknown as { type: string }).type ===
      'reload';

    if (isFirstRequest && !isBrowserReload) return undefined;

    if (fromClaim) return fromClaim;

    const { storageKeys } = this.#config;
    const fromSession =
      this.#config.enableSessionOnReload && sessionStorage.getItem(storageKeys.toid);

    if (fromSession) {
      this.#i18nService
        .t$(`notifications.${RedirectNotifications.KEEP_TOID_IF_RELOAD}`)
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          tap((message) => this.#toastService.info(message)),
        )
        .subscribe();

      return fromSession;
    }

    return undefined;
  }

  /** use storage to sync auth accross tabs */
  #syncAuthenticationStateAccrossTabs() {
    window.addEventListener('storage', (event) => {
      const { onAuthSessionChange } = this.#config.storageKeys;
      if (event.key !== onAuthSessionChange) return;

      const { sub: userId, organizationId } = this.#idTokenClaims() ?? {};

      const eventValues = JSON.parse(event.newValue ?? '{}');

      this.#debug('sync', {
        current: { userId, organizationId },
        event: eventValues,
      });

      if (!userId) return;

      if (userId !== eventValues.userId || organizationId !== eventValues.organizationId) {
        this.#debug('sync : session changed > reloading');
        // Add ?notification param to the URL on reload to display info toast after reload
        const url = new URL(window.location.href);
        url.searchParams.set('notification', RedirectNotifications.SESSION_CHANGED);
        window.location.href = url.toString();
      }
    });
  }

  /** used only for init() flow to check if we have a valid token or not
   * no logic here - just wait for access token to be set or for a failure event
   */
  #waitForToken$(): Observable<boolean> {
    return merge(
      this.accessToken$.pipe(
        filter(Boolean),
        map(() => true),
      ),
      /** may throw to cancel wait if no access token
       * - public : attempt silent, if fail ok no need auth
       * - protected : attempt silent, if fail attempt redirect ; if fail throw error
       */
      this.#msalBroadcastService!.msalSubject$.pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE,
        ),
        switchMap((event) =>
          this.isRouteProtected$().pipe(
            switchMap((isProtected) => {
              if (!isProtected) return throwError(() => new Error('login failed on public route'));

              // ! let handleTokenAcquisitionFailure handle this
              if (event.interactionType === InteractionType.Silent) return EMPTY;

              return throwError(() => new Error('login failed on protected route'));
            }),
            filter((val) => val !== undefined),
          ),
        ),
      ),
      timer(3000).pipe(map(() => !!this.#accessToken())),
    ).pipe(take(1));
  }

  #doLoginRedirect$(request?: Partial<RedirectRequest>): Observable<void> {
    const requestBase = authRequestFactory(AUTH_REQUEST_TYPE.INTERACTIVE, {
      triggerType: 'service',
      environment: this.#env ?? ({} as PlatformEnvExtended),
      config: this.#config,
      lang: this.#language() as AvailableLanguages,
      toid: this.organizationId(),
    });

    const requestMayPromptOrganizationSelection = this.#platformConfigService.get('auth')
      ?.promptOrganizations
      ? {
          state: JSON.stringify({
            promptOrgSelection: true,
          }),
          prompt: 'login',
        }
      : {};

    const authRequest = deepMerge<RedirectRequest>(
      requestBase,
      requestMayPromptOrganizationSelection,
      { ...(request ?? {}) },
    );
    authRequest.state = this.#enrichStateForReplay(authRequest);

    this.#debug('login', JSON.stringify(authRequest));

    return this.#msalService!.loginRedirect(authRequest);
  }

  #runBeforeLoginHook$(): Observable<void> {
    const hook = this.beforeLoginHook();
    if (!hook) return of(undefined);

    const result = hook();
    if (result instanceof Promise) return from(result);
    if (result) return result;

    return of(undefined);
  }

  /** we can fetch state from login success event - but we want to handle it sooner here
   * if the state exists, meaning it comes from a redirect, we do not attempt forceRefresh and thus, avoiding a loop
  /** Early state retreival (not waiting login success event) - allowing
   * - skipping `forceRefresh` because from redirect
   * - catch some errors early (prior to guard/interceptor)
   * `#state=<base64 MSAL meta>|<JSON user state>&error=string`
   */
  #getRedirectState(): void {
    const { hash } = window.location;
    if (!hash) return;

    const stateParam = new URLSearchParams(hash.substring(1)).get('state');
    if (!stateParam) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [msalPart, userPart, ...unknownParts] = stateParam.split('|');
    let replayState: Maybe<AuthStateReplay> = undefined;

    /* TEMPORARY simple implementation
        - here always provide this every auth requests
        - best to be provided in aadb2c app only when needs be (captcha cancel)
          - and be retreived as a 3rd stateParam part
        => CLOUD-2401 */
    try {
      let state = msalPart ? JSON.parse(atob(msalPart)) : {};
      const userState = userPart ? JSON.parse(userPart) : undefined;

      if (userState) {
        replayState = (userState[AUTH_STATE_REPLAY_KEY] as AuthStateReplay | undefined) ?? {};
        // strip our replay key and restore custom user state inside this replay
        // may be used in special login (retry cancelled captcha)
        delete userState[AUTH_STATE_REPLAY_KEY];
        replayState.state = JSON.stringify(userState);

        state = userState;
      }

      this.#state.set(state);
      this.#replayState.set(replayState);
    } catch (error) {
      this.#debug('✗ error decoding state:', error);
    }
  }

  #getAppName$(): Observable<string> {
    return this.#i18nService.onReady$.pipe(
      map(() => this.#platformConfigService.get('app')),
      switchMap(({ title, name }) => {
        if (!title) {
          return name && typeof name !== 'string'
            ? name
            : of((name as string) || window.location.origin);
        }

        return typeof title === 'string' ? of(title) : title;
      }),
    );
  }
}
